import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError } from './api';
import { Modal } from './Modal';
import { ShareTargets } from './ShareDialog';
import { ClipInfo } from './types';

interface Props {
  blob: Blob;
  date: string;
  page: number;
  canSave: boolean; // signed-in people save to their clippings; public visitors only share / download
  onClose: () => void;
}

export function ClipDialog({ blob, date, page, canSave, onClose }: Props) {
  const preview = useMemo(() => URL.createObjectURL(blob), [blob]);
  const [clip, setClip] = useState<ClipInfo | null>(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(canSave);
  const started = useRef(false);

  useEffect(() => () => URL.revokeObjectURL(preview), [preview]);

  // the clipping is saved automatically as soon as it is cut
  useEffect(() => {
    if (!canSave || started.current) return;
    started.current = true;
    api
      .saveClip(blob, date, page)
      .then(setClip)
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not save the clipping.'))
      .finally(() => setSaving(false));
  }, [blob, canSave, date, page]);

  const text = ''; // no caption goes with a shared clipping
  return (
    <Modal title="Your clipping" onClose={onClose} wide>
      <div className="clip-preview">
        <img src={preview} alt="Cropped newspaper clipping" />
      </div>
      {saving && <p className="muted">Saving…</p>}
      {clip && <p className="ok-line">✓ Saved to My clippings</p>}
      {clip && <p className="hint">Clippings are deleted automatically at the end of the day – download or share it before then.</p>}
      {err && <p className="error">{err} You can still share or download it below.</p>}
      <ShareTargets text={text} url={clip ? clip.url : window.location.href} blob={blob} />
      {!clip && !canSave && <p className="muted small">Sign in with an access code to keep clippings and get a link for them.</p>}
    </Modal>
  );
}
